import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, Router, UrlTree } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable } from 'rxjs';
import { AuthService } from '../services/auth.service';

@Injectable({
    providedIn: 'root'
})
export class AuthGuard implements CanActivate {
    constructor(
        private authService: AuthService,
        private router: Router,
        private snackBar: MatSnackBar
    ) { }

    canActivate(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
        const isAuth = this.authService.isAuthenticated();
        const currentUser = this.authService.getCurrentUser();
        console.log('🔒 AuthGuard checking:', state.url, '| Auth:', isAuth, '| User:', currentUser?.username || 'none');

        if (isAuth) {
            // Admin always has full access
            if (this.authService.isAdmin()) {
                console.log('✅ Access granted - Admin user');
                return true;
            }

            const requiredRoles = route.data.roles;
            // Exact privilege names (any one grants access). Use where a module is
            // too coarse: `workflow.view` and `workflow.configure` share the
            // `workflow` module, so a module check would let an approver into
            // configuration. When set, it replaces the privilegeModules check.
            const requiredPrivileges: string[] | undefined = route.data.privileges;
            const hasRoleRule = !!requiredRoles && requiredRoles.length > 0;
            const hasPrivilegeRule = !!requiredPrivileges && requiredPrivileges.length > 0;

            if (hasRoleRule || hasPrivilegeRule) {
                if (hasRoleRule && this.authService.hasAnyRole(requiredRoles)) {
                    console.log('✅ Access granted - User has required roles');
                    return true;
                }
                if (hasPrivilegeRule) {
                    if (requiredPrivileges!.some(p => this.authService.hasPrivilege(p))) {
                        console.log('✅ Access granted - User has required privilege');
                        return true;
                    }
                } else {
                    // Also allow if user has matching privilege modules
                    const privilegeModules = route.data.privilegeModules;
                    if (privilegeModules && privilegeModules.length > 0 && this.authService.hasPrivilegeModule(privilegeModules)) {
                        console.log('✅ Access granted - User has matching privileges');
                        return true;
                    }
                }
                console.log('⚠️ Access denied - User lacks required roles/privileges, redirecting to dashboard');
                // Say why the user landed on the dashboard instead of the page
                // they asked for (a bookmark or a typed URL, since the menu
                // already hides the link).
                const page = route.data.title ? ` to ${route.data.title}` : '';
                this.snackBar.open(`Access Denied: you do not have permission${page}.`, 'Close', {
                    duration: 5000,
                    panelClass: ['error-snackbar']
                });
                return this.router.parseUrl('/dms/dashboard');
            }
            console.log('✅ Access granted - No role restrictions');
            return true;
        }

        // Not authenticated, redirect to login
        console.log('❌ Access denied - Not authenticated, redirecting to login');
        const loginUrl = this.router.parseUrl('/auth/login');
        loginUrl.queryParams = { returnUrl: state.url };
        return loginUrl;
    }
}
